/**
 * Semantic Tailwind class bundles derived from design tokens.
 * Use these instead of repeating raw color/spacing combinations.
 *
 * All classes resolve through CSS variables — theme changes propagate globally.
 */

/** Full-page / layout background */
export const bgApp = "bg-background";

/** Standard elevated panel: cards, sidebars, empty states, map overlays */
export const panel =
  "bg-surface border border-border rounded-lg shadow-md";

/** Compact panel with lighter shadow */
export const panelSm =
  "bg-surface border border-border rounded-lg shadow-sm";

/** Interactive list/card row */
export const panelInteractive =
  "bg-surface hover:bg-surface-hover border border-border rounded-lg shadow-sm transition-all duration-fast";

/** Form field surface (inputs, selects, textareas) */
export const inputSurface =
  "bg-surface-input border border-border-strong text-text rounded-lg text-sm";

/** Form field focus ring — matches existing lgb-blue focus pattern */
export const inputFocus =
  "focus:border-brand focus:ring-1 focus:ring-brand";

/** Standard full-width field (create-order route inputs, dialogs) */
export const fieldFullWidth = `${inputSurface} w-full p-2.5`;

/** Create-order wizard content panel */
export const wizardPanel =
  "bg-surface rounded-lg border border-border-subtle shadow-md p-3 md:p-6";

/** Centered empty-state panel in wizards */
export const wizardPanelCentered =
  "text-center bg-surface rounded-lg border border-border-subtle shadow-md p-3 md:p-8";

/** List row with bottom border (activity feeds, next-up lists) */
export const listRow =
  "flex items-center space-x-4 p-2 py-3 text-sm font-medium border-b border-border hover:bg-surface-hover";

/** Modal / dialog shell */
export const modalSurface =
  "bg-surface-modal border border-border rounded-lg shadow-xl";

/** Modal backdrop */
export const modalOverlay = "bg-overlay";

/** DropdownSearch text input (matches legacy: transparent light, deep dark fill) */
export const dropdownInput =
  "cursor-pointer dark:text-white dark:bg-lgb-dark-background w-full";

/** DropdownSearch results list base (append `bg-white absolute` / panel classes) */
export const dropdownResults =
  "mt-1 z-10 dark:bg-[#080d27] dark:border-lgb-grey-600 dark:text-white w-full";

/** Divider lines between borderless dropdown result rows (DropdownSearchListItem). */
export const dropdownResultsDivided =
  "divide-y divide-gray-200 dark:divide-gray-700";

/** Typography */
export const textHeading = "text-text font-medium";
export const textHeadingLg = "text-text font-semibold";
export const textBody = "text-text";
export const textMuted = "text-muted";
export const textSecondary = "text-secondary";

/** Secondary / outline button surface (matches legacy flowbite "light" button) */
export const btnSecondary =
  "bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-white dark:border-gray-600";

/** Badge base layout (status-specific colors applied on top) */
export const badgeBase =
  "w-max h-min flex items-center gap-2 text-xs font-medium px-2.5 py-1";

/** Inset drop zone (planner available/route zones) */
export const zoneInset =
  "bg-surface-inset p-3 rounded-lg border border-border shadow-[inset_0_0_8px_rgba(0,0,0,0.02)] transition-all duration-fast";

/** Draggable card in planner */
export const cardDraggable =
  "group relative bg-surface hover:bg-surface-hover transition-all duration-fast cursor-move border border-border rounded-lg shadow-sm hover:shadow-md";

/** Table row hover */
export const tableRowHover = "hover:bg-surface-hover";

/** Filter bar clear button */
export const filterClearBtn =
  "flex items-center justify-center p-2.5 text-danger hover:text-danger bg-surface-muted border border-border-strong rounded-lg disabled:opacity-50";

/** Map sidebar overlay shell (orders/drivers list on map) */
export const mapOverlay =
  "bg-surface border border-border rounded-lg shadow-sm h-full flex flex-col overflow-hidden";

/** Map overlay header strip (white panel headers inside map sidebars) */
export const mapOverlayHeader =
  "bg-surface border-b border-border p-3";

/** Page-level tab bar (planner, map pages) — grey layout bg, tabs fill height */
export const pageTabBar =
  "border-b border-border flex-shrink-0 bg-layout";

/** Map overlay toolbar strip */
export const mapOverlayToolbar =
  "p-2 flex-shrink-0 border-b border-border";

/** Section divider */
export const sectionDivider = "border-t border-border my-6";

/** Stat/info card (OrderDetails customer/driver tiles) */
export const cardStat =
  "bg-surface dark:bg-surface-muted rounded-lg p-3 border border-border-light dark:border-border shadow-sm";

/** Clickable stat card */
export const cardStatInteractive = `${cardStat} cursor-pointer hover:bg-surface-hover transition-colors`;

/** KPI / metric card (reports dashboard) */
export const metricCard =
  "bg-surface dark:bg-surface-muted p-4 rounded-lg shadow";

/** Table head cell — visible grey strip (matches legacy gray-200 / slate-700) */
export const tableHeadCell =
  "bg-surface-tableHead text-tableHead font-semibold text-sm sm:text-base";

/** Pagination nav button (enabled) */
export const paginationNavBtn =
  "flex items-center justify-center px-3 h-8 leading-tight border text-secondary bg-surface border-border-strong hover:bg-brand-muted hover:text-brand dark:bg-surface-muted dark:border-border dark:hover:bg-surface-hover dark:hover:text-text";

/** Pagination nav button (disabled) */
export const paginationNavBtnDisabled =
  "flex items-center justify-center px-3 h-8 leading-tight border text-muted bg-surface border-border cursor-not-allowed dark:bg-surface-muted dark:border-border opacity-60";

/** Pagination active page */
export const paginationPageActive =
  "flex items-center justify-center px-3 h-8 leading-tight border text-brand border-brand-muted bg-brand-muted hover:bg-brand-muted";

/** Pagination inactive page */
export const paginationPageInactive =
  "flex items-center justify-center px-3 h-8 leading-tight border text-secondary bg-surface border-border-strong hover:bg-brand-muted hover:text-brand dark:bg-surface-muted dark:border-border dark:hover:bg-surface-hover dark:hover:text-text";

/** List row hover (map driver picker) */
export const listRowHover =
  "hover:bg-surface-hover dark:hover:bg-surface-muted";

/** Highlight box inside modals */
export const highlightBox =
  "mt-2 p-3 bg-surface-muted dark:bg-surface-modal rounded-lg";

/** Modal footer bar */
export const modalFooter =
  "border-t border-border bg-surface";
